import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { History, Trash2, Eye, Battery, Sun, Clock, Loader2, Plus, Building2, GitBranch, User, Copy } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { nl } from 'date-fns/locale';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import type { HubSimulation } from '@/hooks/useHubSimulations';

interface Props {
  simulations: HubSimulation[] | undefined;
  isLoading: boolean;
  onDelete: (id: string) => void;
  onView: (sim: HubSimulation) => void;
  onNew: () => void;
  onDuplicate: (sim: HubSimulation) => void;
}

export function HubSavedSimulations({ simulations, isLoading, onDelete, onView, onNew, onDuplicate }: Props) {
  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-12 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              HUB Simulaties
              {simulations && simulations.length > 0 && <Badge variant="secondary">{simulations.length}</Badge>}
            </CardTitle>
            <CardDescription>
              Beheer en vergelijk energiepark simulaties en business cases
            </CardDescription>
          </div>
          <Button onClick={onNew} className="gap-2">
            <Plus className="h-4 w-4" />
            Nieuwe Simulatie
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {!simulations || simulations.length === 0 ? (
          <div className="py-12 text-center">
            <History className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">
              Nog geen simulaties opgeslagen. Maak een nieuwe simulatie aan.
            </p>
            <Button onClick={onNew} variant="outline" className="gap-2">
              <Plus className="h-4 w-4" />
              Nieuwe Simulatie
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Naam</TableHead>
                  <TableHead>Klant</TableHead>
                  <TableHead>Type</TableHead>
                   <TableHead>Versie</TableHead>
                   <TableHead>Aangemaakt door</TableHead>
                   <TableHead>Datum</TableHead>
                  <TableHead>Configuratie</TableHead>
                  <TableHead className="text-right">Acties</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {simulations.map(sim => {
                  const p = sim.sim_params || {};
                  return (
                    <TableRow key={sim.id} className="cursor-pointer hover:bg-accent/50" onClick={() => onView(sim)}>
                      <TableCell className="font-medium">{sim.name}</TableCell>
                      <TableCell>
                        {sim.org_name ? (
                          <div className="flex items-center gap-1.5">
                            <Building2 className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm">{sim.org_name}</span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {sim.sim_type === 'businesscase' ? '💰 Business Case' : '⚡ Simulatie'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <GitBranch className="h-3 w-3 text-muted-foreground" />
                          <span className="text-sm">v{sim.version}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {sim.creator_name ? (
                          <div className="flex items-center gap-1.5">
                            <User className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm">{sim.creator_name}</span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDistanceToNow(new Date(sim.updated_at), { addSuffix: true, locale: nl })}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {p.pv && (
                            <span className="flex items-center gap-1">
                              <Sun className="h-3 w-3" /> {p.pv} MWp
                            </span>
                          )}
                          {p.bE && (
                            <span className="flex items-center gap-1">
                              <Battery className="h-3 w-3" /> {p.bE} MWh
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-2">
                          <Button variant="ghost" size="icon" onClick={() => onView(sim)} title="Openen">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => onDuplicate(sim)} title="Dupliceren">
                            <Copy className="h-4 w-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" title="Verwijderen">
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Simulatie verwijderen?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Weet u zeker dat u "{sim.name}" wilt verwijderen?
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Annuleren</AlertDialogCancel>
                                <AlertDialogAction onClick={() => onDelete(sim.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                                  Verwijderen
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
